/* FMT project recovery: explicit requests only. No newsletter subscription.
   Klaviyo identify/track record a request; neither confirms email delivery.
   https://developers.klaviyo.com/en/docs/introduction_to_the_klaviyo_object */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.FMTRecovery = factory();
}(typeof window !== 'undefined' ? window : this, function () {
  'use strict';

  var EVENT_NAME = 'FMT Project Saved';
  var CONSENT_VERSION = 'fmt-project-recovery-2026-09-11';
  function clean(value, limit) { return String(value || '').trim().slice(0, limit); }
  function validEmail(value) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) && value.length <= 254; }
  function failure(code, message) { var e = new Error(message); e.code = code; return e; }
  function containsPersonalFields(value) {
    if (!value || typeof value !== 'object') return false;
    return Object.keys(value).some(function (key) {
      return /^(email|name|first_name|last_name|notes|phone|address|marketing|marketingConsent|reminderConsent)$/i.test(key) || containsPersonalFields(value[key]);
    });
  }
  function safeResumeUrl(value, origin) {
    if (typeof value !== 'string' || value.length > 25000) throw failure('invalid_project', 'Your project link could not be prepared. Your choices are still here in the open builder.');
    var url;
    try { url = new URL(value); } catch (_) { throw failure('invalid_project', 'Your project link could not be prepared.'); }
    if (url.protocol !== 'https:' || url.origin !== origin || url.username || url.password || !/^\/pages\/[a-z0-9-]+\/?$/i.test(url.pathname)) {
      throw failure('invalid_project', 'Your project link could not be prepared.');
    }
    var allowed = ['o', 'preview_theme_id'];
    url.searchParams.forEach(function (_, key) {
      if (allowed.indexOf(key) < 0) throw failure('invalid_project', 'Your project link contains information that cannot be shared.');
    });
    var encoded = url.searchParams.get('o');
    if (!encoded || encoded.length > 20000 || !/^[A-Za-z0-9_-]+$/.test(encoded)) throw failure('invalid_project', 'Your project link does not contain a saved project.');
    try {
      var b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      var json = typeof atob === 'function' ? decodeURIComponent(escape(atob(b64))) : Buffer.from(b64, 'base64').toString('utf8');
      var portable = JSON.parse(json);
      if (!portable || Array.isArray(portable) || typeof portable !== 'object' || containsPersonalFields(portable)) throw new Error('unsafe');
    } catch (_) { throw failure('invalid_project', 'Your project link contains information that cannot be shared.'); }
    // Fragment contents are not needed by the established portable-order format.
    url.hash = '';
    return url.href;
  }

  function create(options) {
    options = options || {};
    if (typeof options.getProject !== 'function') throw new Error('FMTRecovery requires getProject.');
    var origin = options.origin || (typeof window !== 'undefined' && window.location.origin) || 'https://familymediatransfer.com';
    var timeoutMs = Math.max(10, Math.min(30000, Number(options.timeoutMs) || 10000));
    var delayMs = Math.max(0, Number(options.updateDelayMs) || 750);
    var now = options.now || function () { return new Date().toISOString(); };
    var client = options.getClient || function () { return typeof window !== 'undefined' ? window.klaviyo : null; };
    var storage = options.storage || null; // Optional storage is supplied by the builder; no PII is persisted by default.
    var storageKey = options.storageKey || 'fmt_recovery_requests_v1';
    var records = {}, capture = null, inFlight = null, inFlightKey = '', updateTimer = null, disposed = false, generation = 0, withdrawing = false;
    var state = { status: 'idle', message: '', email: '', resumeUrl: '', emailSent: false };
    try { if (storage) records = JSON.parse(storage.getItem(storageKey) || '{}'); } catch (_) { records = {}; }
    if (!records || typeof records !== 'object' || Array.isArray(records)) records = {};
    function remember() {
      if (!storage) return;
      try {
        var keys = Object.keys(records);
        if (keys.length > 30) keys.slice(0, keys.length - 30).forEach(function (key) { delete records[key]; });
        storage.setItem(storageKey, JSON.stringify(records));
      } catch (_) {}
    }
    function publish(status, message, extra) {
      state = Object.assign({}, state, extra || {}, { status: status, message: message, emailSent: false });
      if (!disposed && typeof options.onStatus === 'function') { try { options.onStatus(Object.assign({}, state)); } catch (_) {} }
      return Object.assign({}, state);
    }
    function project() {
      var p = options.getProject() || {};
      var id = clean(p.id, 100);
      if (!/^[a-zA-Z0-9_-]{8,100}$/.test(id)) throw failure('invalid_project', 'Your project could not be prepared. Your choices are still here in the open builder.');
      return {
        id: id, resumeUrl: safeResumeUrl(p.resumeUrl, origin),
        estimate: Number.isFinite(Number(p.estimate)) ? Math.round(Math.max(0, Math.min(10000000, Number(p.estimate))) * 100) / 100 : 0,
        stage: clean(p.stage, 64), formats: Array.isArray(p.formats) ? p.formats.slice(0, 20).map(function (f) { return clean(f, 64); }) : []
      };
    }
    function sdkCall(api, method, args) {
      return new Promise(function (resolve, reject) {
        var settled = false;
        var timer = setTimeout(function () { end(failure('timeout', 'The save service did not confirm the request in time.')); }, timeoutMs);
        function end(error, result) {
          if (settled) return;
          settled = true; clearTimeout(timer);
          if (error) reject(error); else resolve(result);
        }
        try {
          var ret = api[method].apply(api, args.concat(function (result) { end(null, result); }));
          if (ret && typeof ret.then === 'function') ret.then(function (value) { end(null, value); }, function (error) { end(error && error.code === 'rejected' ? error : failure('network', 'The save service could not be reached.')); });
        } catch (_) { end(failure('network', 'The save service could not be reached.')); }
      });
    }
    function getApi() {
      var api;
      try { api = client(); } catch (_) {}
      if (!api || typeof api.identify !== 'function' || typeof api.track !== 'function') throw failure('unavailable', 'Email saving is temporarily unavailable. Your choices are still here; try saving again later or continue without email.');
      return api;
    }
    function profile(person, p, includeConsent) {
      var parts = person.name.split(/\s+/).filter(Boolean);
      var data = {
        email: person.email, fmt_project_id: p.id, fmt_project_resume_url: p.resumeUrl,
        fmt_project_updated_at: now(), fmt_project_estimate: p.estimate, fmt_project_stage: p.stage,
        fmt_project_formats: p.formats, fmt_project_source: 'builder-v4'
      };
      // Only a fresh explicit request may assert permission. A delayed metadata
      // update must never undo a later withdrawal or a preference changed elsewhere.
      if (includeConsent) {
        data.fmt_project_email_requested = true; data.fmt_project_reminders_requested = true;
        data.fmt_project_consent_version = CONSENT_VERSION; data.fmt_project_consent_at = person.consentAt;
      }
      if (parts.length) data.first_name = parts[0];
      if (parts.length > 1) data.last_name = parts.slice(1).join(' ');
      // General marketing consent is intentionally neither asserted nor overwritten.
      return data;
    }
    function acknowledgedIdentify(result) {
      return result && typeof result === 'object' && result.success !== false && !result.error &&
        !(Array.isArray(result.errors) ? result.errors.length : result.errors);
    }
    function keyFor(email, p) { return p.id + ':' + email; }
    function fingerprint(p) { return JSON.stringify(p); }
    function getContext() {
      if (!capture) return null;
      var record = records[keyFor(capture.person.email, { id: capture.projectId })];
      if (!record || record.status !== 'saved') return null;
      return { version: 1, consentVersion: CONSENT_VERSION, projectId: capture.projectId,
        person: Object.assign({}, capture.person), fingerprint: capture.fingerprint };
    }
    function contextChanged() {
      if (typeof options.onCaptureChange === 'function') {
        try { options.onCaptureChange(getContext()); } catch (_) {}
      }
    }
    // Only the current, unexpired visit supplies this context. A URL never does.
    // Require an acknowledged ledger entry; restoring permission must not send an event.
    try {
      var restored = options.restoredCapture, current = project();
      if (restored && restored.version === 1 && restored.consentVersion === CONSENT_VERSION &&
          restored.projectId === current.id && restored.person &&
          validEmail(restored.person.email) && restored.person.email === clean(restored.person.email, 255).toLowerCase() &&
          typeof restored.person.name === 'string' && restored.person.name.length <= 160 &&
          Number.isFinite(Date.parse(restored.person.consentAt)) && Date.parse(restored.person.consentAt) <= Date.parse(now()) &&
          typeof restored.fingerprint === 'string' && restored.fingerprint.length <= 30000 &&
          records[keyFor(restored.person.email, current)] && records[keyFor(restored.person.email, current)].status === 'saved') {
        capture = { person: { email: restored.person.email, name: restored.person.name, consentAt: restored.person.consentAt },
          projectId: current.id, fingerprint: restored.fingerprint };
        state = Object.assign({}, state, { status: 'saved', message: 'Your project email request is saved for this visit.', email: capture.person.email, resumeUrl: current.resumeUrl });
      }
    } catch (_) { /* Invalid recovery context cannot attach an email to a project. */ }
    function scheduleUpdate() {
      if (updateTimer) clearTimeout(updateTimer);
      updateTimer = setTimeout(function () { updateTimer = null; update(); }, delayMs);
    }
    function submit(input) {
      input = input || {};
      if (withdrawing) return Promise.resolve(publish('busy', 'Your email preference is still updating. Please try again in a moment.'));
      var email = clean(input.email, 255).toLowerCase();
      if (!validEmail(email)) return Promise.resolve(publish('invalid', 'Enter a valid email address to save your project.', { email: email }));
      if (input.reminderConsent !== true) return Promise.resolve(publish('invalid', 'Choose whether you want a project link and reminders, or continue without email saving.', { email: email }));
      if (disposed) return Promise.resolve(publish('idle', ''));
      var p, api;
      try { p = project(); api = getApi(); } catch (error) { return Promise.resolve(publish(error.code || 'error', error.message, { email: email })); }
      var key = keyFor(email, p);
      if (inFlight) {
        if (inFlightKey === key) return inFlight;
        return Promise.resolve({ status: 'busy', message: 'Your previous save is still finishing. Please try again in a moment.', emailSent: false });
      }
      var record = records[key];
      if (record && (record.status === 'pending' || record.status === 'uncertain')) {
        return Promise.resolve(publish('uncertain', 'Your email request may already be recorded. We are checking its status. You can continue with your project.', { email: email, resumeUrl: p.resumeUrl }));
      }
      var person = { email: email, name: clean(input.name, 160), consentAt: now() };
      var version = ++generation, eventAttempted = false;
      publish('saving', 'Saving your project…', { email: email, resumeUrl: p.resumeUrl });
      inFlightKey = key;
      inFlight = (async function () {
        try {
          var identified = await sdkCall(api, 'identify', [profile(person, p, true)]);
          if (!acknowledgedIdentify(identified)) throw failure('error', 'The email service did not confirm your saved details. Your choices are still here; try saving again later or continue without email.');
          if (version !== generation || disposed) return Object.assign({}, state);
          capture = { person: person, projectId: p.id, fingerprint: fingerprint(p) };
          if (!record || record.status !== 'saved') {
            var eventId = record && record.eventId || ('fmt_saved_' + p.id + '_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10));
            records[key] = { status: 'pending', eventId: eventId }; remember();
            eventAttempted = true;
            var tracked = await sdkCall(api, 'track', [EVENT_NAME, {
              projectId: p.id, resumeUrl: p.resumeUrl, estimate: p.estimate, stage: p.stage, formats: p.formats,
              reminderConsent: true, consentVersion: CONSENT_VERSION, consentAt: person.consentAt,
              source: 'builder-v4', requestId: eventId, $event_id: eventId
            }]);
            if (tracked !== true) { records[key].status = 'failed'; remember(); throw failure('error', 'Your details were saved, but the email request was not confirmed. You can retry saving or continue with your project.'); }
            records[key].status = 'saved'; remember();
          }
          if (version !== generation || disposed) return Object.assign({}, state);
          contextChanged();
          var result = publish('saved', 'Your project is saved for follow-up. You can keep going and use your saved email link to return later.', { email: email, resumeUrl: p.resumeUrl });
          try { if (fingerprint(project()) !== capture.fingerprint) scheduleUpdate(); } catch (_) {}
          return result;
        } catch (error) {
          var uncertain = eventAttempted && (error.code === 'timeout' || error.code === 'network');
          if (uncertain && records[key]) { records[key].status = 'uncertain'; remember(); }
          else if (eventAttempted && records[key]) { records[key].status = 'failed'; remember(); }
          if (version !== generation || disposed) return Object.assign({}, state);
          return publish(uncertain ? 'uncertain' : (error.code || 'error'), uncertain ? 'Your email request may have been recorded, but we could not confirm it. Your choices are still here in this tab. You can continue with your project.' : error.message, { email: email, resumeUrl: p.resumeUrl });
        } finally { inFlight = null; inFlightKey = ''; }
      }());
      return inFlight;
    }
    function update() {
      if (!capture || disposed) return Promise.resolve(Object.assign({}, state));
      if (inFlight) { scheduleUpdate(); return inFlight; }
      var p, api;
      try { p = project(); api = getApi(); } catch (_) { return Promise.resolve(Object.assign({}, state)); }
      // A new project needs its own explicit save; do not inherit a prior project's request.
      if (p.id !== capture.projectId || fingerprint(p) === capture.fingerprint) return Promise.resolve(Object.assign({}, state));
      var active = capture, version = generation;
      inFlightKey = keyFor(active.person.email, p);
      inFlight = (async function () {
        try {
          var identified = await sdkCall(api, 'identify', [profile(active.person, p, false)]);
          if (!acknowledgedIdentify(identified)) throw new Error('Unconfirmed');
          if (version !== generation || disposed) return Object.assign({}, state);
          active.fingerprint = fingerprint(p);
          contextChanged();
          // Updating metadata must not imply that a failed/unconfirmed event succeeded.
          var request = records[keyFor(active.person.email, p)];
          var requestConfirmed = request && request.status === 'saved';
          var next = publish(requestConfirmed ? 'saved' : state.status, requestConfirmed ? 'Your saved project is up to date.' : state.message, { resumeUrl: p.resumeUrl });
          try { if (fingerprint(project()) !== active.fingerprint) scheduleUpdate(); } catch (_) {}
          return next;
        } catch (_) {
          if (version !== generation || disposed) return Object.assign({}, state);
          return publish('update_failed', 'Your latest changes are still here. Try saving again to update your email record.', { resumeUrl: p.resumeUrl });
        } finally { inFlight = null; inFlightKey = ''; }
      }());
      return inFlight;
    }
    function clear() {
      generation++; capture = null;
      if (updateTimer) clearTimeout(updateTimer);
      updateTimer = null; contextChanged();
      return publish('idle', '', { email: '', resumeUrl: '' });
    }
    async function withdraw() {
      if (withdrawing) return Object.assign({}, state);
      var previous = capture, pending = inFlight;
      clear();
      if (!previous) return Object.assign({}, state);
      withdrawing = true;
      var version = generation;
      publish('withdrawing', 'Updating your project email preference…');
      // Let an older identify finish before recording the withdrawal, so it cannot
      // silently restore the flags after this request succeeds.
      if (pending) { try { await pending; } catch (_) {} }
      // Clearing fields or starting a fresh local project must not cancel this
      // already requested preference change. Generation guards only its UI result.
      try {
        var api = getApi();
        var result = await sdkCall(api, 'identify', [{ email: previous.person.email,
          fmt_project_email_requested: false, fmt_project_reminders_requested: false,
          fmt_project_consent_withdrawn_at: now() }]);
        if (!acknowledgedIdentify(result)) throw new Error('Unconfirmed');
        if (version !== generation || disposed) return Object.assign({}, state);
        return publish('withdrawn', 'Your project reminder preference is updated. An email already sent cannot be recalled.');
      } catch (_) {
        if (version !== generation || disposed) return Object.assign({}, state);
        return publish('withdraw_failed', 'We could not confirm that project reminders stopped. Use the unsubscribe link in a project email to stop emails, or contact us for help.');
      } finally { withdrawing = false; }
    }
    return {
      submit: submit, update: update,
      getContext: getContext, withdraw: withdraw,
      getStatus: function () { return Object.assign({}, state); },
      clear: clear,
      destroy: function () { disposed = true; generation++; capture = null; if (updateTimer) clearTimeout(updateTimer); }
    };
  }
  return { create: create, safeResumeUrl: safeResumeUrl, validEmail: validEmail, eventName: EVENT_NAME, consentVersion: CONSENT_VERSION };
}));
